import React, { useMemo } from 'react';
import {
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { getTimezoneLabel } from '../../../../utils/timezoneUtils';

/**
 * Distinct crew home-base cities from workLocation.city
 */
export function collectCrewBaseCities(crewMembers = []) {
  return [
    ...new Set(
      (crewMembers || [])
        .map((c) => String(c?.workLocation?.city || '').trim())
        .filter(Boolean)
    )
  ].sort((a, b) => a.localeCompare(b));
}

export function crewMatchesBaseCity(crew, baseCity) {
  if (!baseCity) return true;
  const city = String(crew?.workLocation?.city || '').trim().toLowerCase();
  return city === String(baseCity).trim().toLowerCase();
}

export function entryMatchesBaseCity(entry, baseCity, crewById) {
  if (!baseCity) return true;
  const crew =
    (entry?.crewId && typeof entry.crewId === 'object' && entry.crewId) ||
    (crewById && crewById.get(String(entry?.crewId?._id || entry?.crewId))) ||
    null;
  if (crew) return crewMatchesBaseCity(crew, baseCity);
  return false;
}

/**
 * Filters component for Crew Duty Roster
 */
const CrewDutyRosterFilters = ({
  filters,
  setFilters,
  crewMembers,
  tabValue,
  displayTimezone,
  hideCrewFilter = false,
  showBaseFilter = true
}) => {
  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const baseCities = useMemo(() => collectCrewBaseCities(crewMembers), [crewMembers]);

  // Hide on Crew Hours (ops tab 3), My Hours / Published (crew tabs 1–2), Availability/Policies/DGCA/Published (ops >4).
  // Rostering (4) still uses crew/date/base filters.
  if (tabValue === 3 || tabValue > 4 || (hideCrewFilter && tabValue >= 1)) {
    return null;
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2}>
        {!hideCrewFilter && (
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Crew Member</InputLabel>
              <Select
                value={filters.crewId}
                label="Crew Member"
                onChange={(e) => setFilters({ ...filters, crewId: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {crewMembers
                  .filter((crew) => crewMatchesBaseCity(crew, filters.baseCity))
                  .map((crew) => (
                    <MenuItem key={crew._id} value={crew._id}>
                      {crew.firstName} {crew.lastName}
                      {crew.workLocation?.city ? ` (${crew.workLocation.city})` : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {showBaseFilter && !hideCrewFilter && (
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Base</InputLabel>
              <Select
                value={filters.baseCity || ''}
                label="Base"
                onChange={(e) => setFilters({ ...filters, baseCity: e.target.value })}
              >
                <MenuItem value="">All bases</MenuItem>
                {baseCities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Duty Type</InputLabel>
            <Select
              value={filters.dutyType}
              label="Duty Type"
              onChange={(e) => setFilters({ ...filters, dutyType: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="FLIGHT">Flight</MenuItem>
              <MenuItem value="TRAINING">Training</MenuItem>
              <MenuItem value="STANDBY">Standby</MenuItem>
              <MenuItem value="RESERVE">Reserve</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
              <MenuItem value="SCHEDULED_DUTY_OFF">Scheduled off (plan)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="date"
            label={tzLabel ? `Start Date (${tzLabel})` : 'Start Date'}
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="date"
            label={tzLabel ? `End Date (${tzLabel})` : 'End Date'}
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CrewDutyRosterFilters;
